import { HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

/** F127 — one-time first-run welcome card shown before the wizard timeline.
 *
 * Frames the 5-step setup as guided + at-the-owner's-pace (no "minutes"
 * promise — steps 4–5 depend on Instagram OAuth + payment), mentions the
 * per-field `?` help (F126), and dismisses via a single primary CTA. The
 * card absorbs the page greeting on first run, so `BotPage` renders this
 * INSTEAD of the greeting + timeline (FR1).
 *
 * Design (FR6): `max-w-2xl mx-auto`, border-not-shadow (inline content),
 * monochrome — numeral circles + help block use `bg-muted`, no accent.
 */

/** Wizard steps in order — keys map to `onboarding.welcome.steps.<key>`. */
const STEP_KEYS = ['info', 'kb', 'preview', 'meta_oauth', 'stripe'] as const;

interface WelcomeCardProps {
  /** Owner first name for the greeting. */
  name: string;
  /** Dismiss the card + reveal the timeline (optimistic — see context). */
  onStart: () => void;
}

export function WelcomeCard({ name, onStart }: WelcomeCardProps) {
  const { t } = useTranslation();

  return (
    <Card data-testid="welcome-card" className="mx-auto max-w-2xl space-y-6 p-6 sm:p-8">
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('onboarding.welcome.title', { name })}
        </h1>
        <p className="text-sm text-muted-foreground">{t('onboarding.welcome.subtitle')}</p>
      </div>

      <ol className="space-y-3">
        {STEP_KEYS.map((key, index) => (
          <li key={key} className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground"
            >
              {index + 1}
            </span>
            <div className="space-y-0.5">
              <p className="text-sm font-medium leading-tight">
                {t(`onboarding.welcome.steps.${key}.label`)}
              </p>
              <p className="text-sm text-muted-foreground leading-tight">
                {t(`onboarding.welcome.steps.${key}.caption`)}
              </p>
            </div>
          </li>
        ))}
      </ol>

      <div className="flex items-start gap-3 rounded-lg bg-muted/50 p-4">
        <HelpCircle className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">{t('onboarding.welcome.help_note')}</p>
      </div>

      <Button
        data-testid="welcome-card-start"
        onClick={onStart}
        className="min-h-11 w-full sm:w-auto"
      >
        {t('onboarding.welcome.cta_start')}
      </Button>
    </Card>
  );
}
